import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getIAMUsers } from "@/services/firebase/iam";
import { UserWithRole } from "@/types/iam";
import UserRoleManager from "@/components/iam/UserRoleManager";
import AddUserDialog from "@/components/iam/AddUserDialog";
import { usePermissions } from "@/hooks/usePermissions";
import {
  AdminPage,
  AdminPageHeader,
  AdminLoadingSkeleton,
  AdminErrorAlert,
  AdminSearchInput,
  AdminDataTable,
} from "@/components/admin-ui";
import { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { Badge } from "@/components/ui/badge";
import { UserPlus } from "lucide-react";

export default function IAMPage() {
  const { canManageIAM } = usePermissions();
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const queryClient = useQueryClient();

  // 從 Firestore iam 集合獲取用戶列表（包含 admin、staff、developer 和 observer）
  const { data: users, isLoading, error } = useQuery({
    queryKey: ["iam-users"],
    queryFn: () => getIAMUsers(),
  });

  // 過濾用戶
  const filteredUsers = useMemo(() => {
    if (!users) return [];

    if (!searchTerm) return users;

    const term = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.email?.toLowerCase().includes(term) ||
        user.displayName?.toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  const handleUpdate = () => {
    // 刷新 IAM 用戶列表
    queryClient.invalidateQueries({ queryKey: ["iam-users"] });
  };

  if (!canManageIAM) {
    return (
      <AdminPage>
        <AdminErrorAlert message="您沒有權限訪問此頁面。只有管理員可以管理 IAM。" />
      </AdminPage>
    );
  }

  const columns: AdminTableColumn[] = [
    { key: "user", label: "用戶" },
    { key: "role", label: "當前角色" },
    { key: "actions", label: "操作" },
  ];

  const renderCell = (user: UserWithRole, column: AdminTableColumn) => {
    switch (column.key) {
      case "user":
        return (
          <div className="flex items-center gap-3">
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName || "User"}
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
                {user.displayName?.[0] || user.email?.[0] || "U"}
              </div>
            )}
            <div>
              <div className="text-sm font-medium text-foreground">
                {user.displayName || "未設定名稱"}
              </div>
              <div className="text-xs text-muted-foreground">
                {user.email}
              </div>
            </div>
          </div>
        );
      case "role":
        return (
          <div className="flex items-center gap-2 flex-wrap">
            {user.isAdmin && (
              <Badge variant="destructive">Admin</Badge>
            )}
            {user.isStaff && !user.isAdmin && (
              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300 hover:bg-blue-100">
                Staff
              </Badge>
            )}
            {user.isDeveloper && (
              <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300 hover:bg-purple-100">
                Developer
              </Badge>
            )}
            {user.isObserver && (
              <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300 hover:bg-green-100">
                Observer
              </Badge>
            )}
            {!user.isAdmin && !user.isStaff && !user.isDeveloper && !user.isObserver && (
              <Badge variant="secondary">一般用戶</Badge>
            )}
          </div>
        );
      case "actions":
        return <UserRoleManager user={user} onUpdate={handleUpdate} />;
      default:
        return null;
    }
  };

  return (
    <AdminPage>
      <AdminPageHeader
        title="用戶角色管理"
        onCreate={() => setShowAddDialog(true)}
        createLabel="新增用戶"
      />

      <div className="mb-4">
        <AdminSearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="搜索用戶（email 或名稱）..."
          className="max-w-md"
        />
      </div>

      <AdminDataTable
        columns={columns}
        data={filteredUsers}
        loading={isLoading}
        emptyIcon={<UserPlus className="h-5 w-5" />}
        emptyTitle={searchTerm ? "找不到符合條件的用戶" : "目前沒有權限用戶，請使用「新增用戶」按鈕添加"}
        renderCell={renderCell}
        getRowId={(user) => user.id}
      />

      {error && (
        <AdminErrorAlert
          message={`載入用戶時發生錯誤：${error instanceof Error ? error.message : "未知錯誤"}`}
        />
      )}

      {/* 新增用戶對話框 */}
      <AddUserDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSuccess={handleUpdate}
      />
    </AdminPage>
  );
}
